import { getUnlocAccessToken } from './dataverseService';
import { getActiveUnlocProjectId, getUnlocApimBaseUrl } from '../config/unlocApi';
import { normalizeMobileNumber } from '../config/keyRequest';

async function readApimError(response, fallback) {
  try {
    const text = await response.text();
    if (!text) {
      return `${fallback} (${response.status}).`;
    }

    try {
      const json = JSON.parse(text);
      return json.error || json.message || json.error_description || text;
    } catch {
      return text;
    }
  } catch {
    return `${fallback} (${response.status}).`;
  }
}

function requireMobile(mobileNumber) {
  const mobile = normalizeMobileNumber(mobileNumber);
  if (!mobile) {
    throw new Error('Ugyldig mobilnummer. Bruk norsk format, f.eks. +4791750101.');
  }
  return mobile;
}

function requireProjectId() {
  const projectId = getActiveUnlocProjectId();
  if (!projectId) {
    throw new Error('Unloc-prosjekt er ikke satt for denne kommunen.');
  }
  return projectId;
}

async function unlocFetch(
  path,
  { method = 'GET', query, body, fallback = 'Unloc-kall feilet' } = {}
) {
  const token = await getUnlocAccessToken();
  if (!token) {
    return null;
  }

  const params = new URLSearchParams();
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, String(value));
      }
    }
  }

  const qs = params.toString();
  const url = `${getUnlocApimBaseUrl()}${path}${qs ? `?${qs}` : ''}`;
  const headers = {
    Authorization: `Bearer ${token}`
  };

  let requestBody;
  if (body !== undefined && body !== null) {
    headers['Content-Type'] = 'application/json';
    requestBody = typeof body === 'string' ? body : JSON.stringify(body);
  }

  const response = await fetch(url, {
    method,
    headers,
    body: requestBody
  });

  if (!response.ok) {
    throw new Error(await readApimError(response, fallback));
  }

  const text = await response.text();
  if (!text) {
    return {};
  }

  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
}

function projectKeysPath() {
  return `/v2/projects/${encodeURIComponent(requireProjectId())}/keys`;
}

function projectKeyPath(keyId) {
  return `/v2/projects/${encodeURIComponent(requireProjectId())}/key/${encodeURIComponent(keyId)}`;
}

export async function listKeysByMobile(mobileNumber) {
  const mobile = requireMobile(mobileNumber);
  return unlocFetch(projectKeysPath(), {
    query: { appUserId: mobile, limit: 2500 },
    fallback: 'Unloc-oppslag feilet'
  });
}

export async function listAllKeys() {
  const keys = [];
  let startAfterId;

  for (let page = 0; page < 20; page += 1) {
    const result = await unlocFetch(projectKeysPath(), {
      query: { limit: 2500, startAfterId },
      fallback: 'Unloc-oppslag for prosjektet feilet'
    });

    if (!result) {
      return null;
    }

    const batch = result.keys || result.value || [];
    keys.push(...batch);

    if (batch.length < 2500) {
      break;
    }

    startAfterId = batch[batch.length - 1]?.id;
    if (!startAfterId) {
      break;
    }
  }

  return { keys };
}

function projectLocksPath() {
  return `/v2/projects/${encodeURIComponent(requireProjectId())}/locks`;
}

/** Paginated GetLocks from Unloc via APIM (same as Power Automate GetLocks). */
export async function listAllLocks() {
  const locks = [];
  let startAfterId;

  for (let page = 0; page < 20; page += 1) {
    const result = await unlocFetch(projectLocksPath(), {
      query: { limit: 2500, startAfterId },
      fallback: 'Unloc-oppslag for låser feilet'
    });

    if (!result) {
      return null;
    }

    const batch = result.locks || result.value || [];
    locks.push(...batch);

    if (batch.length < 2500) {
      break;
    }

    startAfterId = batch[batch.length - 1]?.id;
    if (!startAfterId) {
      break;
    }
  }

  return { locks };
}

export async function listKeysByLock(lockId) {
  if (!lockId) {
    throw new Error('Lås-ID mangler.');
  }

  return unlocFetch(projectKeysPath(), {
    query: { lockId, limit: 2500 },
    fallback: 'Unloc-oppslag for lås feilet'
  });
}

/**
 * Create Unloc keys (MobilLogon-equivalent). Pass durationMinutes for timed keys;
 * omit for persistent keys (oc_lagrenokler).
 */
export async function createKeys({
  appUserId,
  lockIds = [],
  durationMinutes
} = {}) {
  const mobile = requireMobile(appUserId);
  const uniqueLockIds = [...new Set((lockIds || []).filter(Boolean))];
  if (!uniqueLockIds.length) {
    return { keys: [] };
  }

  const keys = uniqueLockIds.map((lockId) => {
    const entry = {
      lockId,
      appUserId: mobile,
      // Session logon must not SMS/push the invitee (same as MobilLogon CreateKeys).
      skipInviteNotification: true
    };
    if (durationMinutes != null && durationMinutes !== '') {
      entry.durationMinutes = Number(durationMinutes);
    }
    return entry;
  });

  // Unloc accepts batches; chunk to stay under typical payload limits.
  const chunkSize = 100;
  let lastResult = { keys: [] };
  for (let i = 0; i < keys.length; i += chunkSize) {
    const batch = keys.slice(i, i + chunkSize);
    lastResult = await unlocFetch(projectKeysPath(), {
      method: 'POST',
      body: { keys: batch },
      fallback: 'Kunne ikke opprette Unloc-nøkler'
    });
    if (lastResult === null) {
      return null;
    }
  }

  return lastResult || { keys };
}

export async function revokeAllKeysByMobile(mobileNumber) {
  const mobile = requireMobile(mobileNumber);
  return unlocFetch(projectKeysPath(), {
    method: 'DELETE',
    query: {
      appUserId: mobile,
      excludeSubprojectKeys: true,
      notifyInvitee: false
    },
    fallback: 'Kunne ikke trekke tilbake nøkler'
  });
}

export async function revokeKeyById(keyId, { mobileNumber, lockId } = {}) {
  if (lockId && mobileNumber) {
    return revokeKeysForLock(mobileNumber, lockId);
  }

  if (!keyId) {
    throw new Error('Nøkkel-ID mangler.');
  }

  return unlocFetch(projectKeyPath(keyId), {
    method: 'DELETE',
    fallback: 'Kunne ikke trekke tilbake nøkkelen'
  });
}

export async function revokeKeysForLock(mobileNumber, lockId) {
  const mobile = requireMobile(mobileNumber);
  if (!lockId) {
    throw new Error('Lås-ID mangler.');
  }

  return unlocFetch(projectKeysPath(), {
    method: 'DELETE',
    query: {
      appUserId: mobile,
      lockId,
      excludeSubprojectKeys: true,
      notifyInvitee: false
    },
    fallback: 'Kunne ikke trekke tilbake nøkler for låsen'
  });
}

function projectAccessGroupsPath() {
  return `/v2/projects/${encodeURIComponent(requireProjectId())}/access-groups`;
}

function projectAccessGroupPath(groupId) {
  return `${projectAccessGroupsPath()}/${encodeURIComponent(groupId)}`;
}

export async function listAccessGroups() {
  const result = await unlocFetch(projectAccessGroupsPath(), {
    fallback: 'Unloc-oppslag for tilgangsgrupper feilet'
  });

  if (!result) {
    return null;
  }

  const accessGroups = result.accessGroups || result.groups || result.value || [];
  return { accessGroups };
}

export async function getAccessGroup(groupId) {
  if (!groupId) {
    throw new Error('Tilgangsgruppe-ID mangler.');
  }

  return unlocFetch(projectAccessGroupPath(groupId), {
    fallback: 'Kunne ikke hente tilgangsgruppe fra Unloc'
  });
}

function projectManagedUsersPath() {
  return `/v2/projects/${encodeURIComponent(requireProjectId())}/managed-users`;
}

function splitDisplayName(name) {
  const trimmed = String(name || '').trim();
  if (!trimmed) {
    return { firstName: 'OpenCare', lastName: 'User' };
  }

  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: 'User' };
  }

  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' ')
  };
}

function isManagedUserAlreadyExistsError(error) {
  const message = String(error?.message || '').toLowerCase();
  return (
    message.includes('already exists') ||
    message.includes('duplicate') ||
    message.includes('conflict') ||
    message.includes('409')
  );
}

export async function listManagedUsers() {
  const managedUsers = [];
  let startAfterId;

  for (let page = 0; page < 20; page += 1) {
    const result = await unlocFetch(projectManagedUsersPath(), {
      query: {
        limit: 2500,
        startAfterId,
        excludeUsersFromSubprojects: true
      },
      fallback: 'Kunne ikke hente brukere fra Unloc'
    });

    if (!result) {
      return null;
    }

    const batch = result.managedUsers || result.value || [];
    managedUsers.push(...batch);

    const nextStartAfterId = result.startAfterId || batch[batch.length - 1]?.id;
    if (!batch.length || batch.length < 2500 || !nextStartAfterId || nextStartAfterId === startAfterId) {
      break;
    }

    startAfterId = nextStartAfterId;
  }

  return { managedUsers };
}

export async function createManagedUser({ mobileNumber, name }) {
  const mobile = requireMobile(mobileNumber);
  const { firstName, lastName } = splitDisplayName(name);

  return unlocFetch(projectManagedUsersPath(), {
    method: 'POST',
    body: {
      appUserId: mobile,
      firstName,
      lastName
    },
    fallback: 'Kunne ikke opprette bruker i Unloc'
  });
}

export async function ensureManagedUserInUnloc({ mobileNumber, name }) {
  try {
    await createManagedUser({ mobileNumber, name });
    return { created: true, existed: false };
  } catch (error) {
    if (isManagedUserAlreadyExistsError(error)) {
      return { created: false, existed: true };
    }
    throw error;
  }
}
